import { useCallback, useEffect, useRef } from "react";
import { Extension, combineTransactionSteps, getMarkRange } from "@tiptap/core";
import { DOMParser as SchemaParser } from "@tiptap/pm/model";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import instance from "api/globalaxios";
import {
  isPageBreak,
  startsPage,
} from "Components/RichTextEditor/extensions/PageBreak";
import { BUILT_IN_ITEMS } from "Components/RichTextEditor/toolbarItems";
import {
  formatStyle,
  parseStyle,
} from "Components/RichTextEditor/extensions/styleProps";
import {
  applyPostMatterHtml,
  applyPostedForStage,
  isPostMatterText,
  linkIdOf,
  templateSignature,
  withLinkId,
} from "./orderSheetHtml";

/**
 * One "Post this matter" link per page of the order sheet.
 *
 * A page is a run of blocks in the order column (or in the sheet, outside a
 * table) up to the next Page break or New page - see PageBreak.js. Every page
 * ends the way the template's page 1 does: its "Post this matter" line, then
 * the signature lines under it. PostMatterLinks gives every page the drafter
 * adds a fresh link and the signature lines; usePostMatterLinks
 * writes a booking back into the one link it was made through. The links tell
 * each other apart by the id in their href (see LINK_ID_PARAM).
 */

const KEY = new PluginKey("postMatterLinks");
const LINK_TEXT = "Post this matter";
// Where a page break can start a page. A break nested any deeper (in a list,
// say) gets no link of its own.
const CONTAINERS = ["doc", "tableCell", "tableHeader"];

export const newLinkId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

const BREAK_PROPS = ["page-break-before", "break-before"];

const withoutBreak = (style) =>
  formatStyle(
    parseStyle(style).filter(([property]) => BREAK_PROPS.indexOf(property) === -1)
  ) || null;

// What PageBreak.js writes.
const withBreak = (style) =>
  formatStyle(
    parseStyle(style)
      .filter(([property]) => BREAK_PROPS.indexOf(property) === -1)
      .concat([["page-break-before", "always"]])
  );

// The textblocks of `node`, `node` itself included.
function eachTextblock(node, fn) {
  if (node.isTextblock) {
    fn(node);
    return;
  }
  node.descendants((child) => {
    if (!child.isTextblock) return true;
    fn(child);
    return false;
  });
}

/**
 * The first "Post this matter" link in `node`, as { block, mark }: a textblock
 * that says "Post this matter" and carries a link. Matched on the block's
 * text, so a link the drafter has partly bolded still counts.
 */
function findLink(node, linkType) {
  let found = null;
  eachTextblock(node, (block) => {
    if (found || !isPostMatterText(block.textContent)) return;
    block.forEach((inline) => {
      const mark = !found && linkType.isInSet(inline.marks);
      if (mark) found = { block, mark };
    });
  });
  return found;
}

/**
 * The signature lines at the end of `nodes` - the lines under a page's link:
 * the trailing run of right-aligned lines, with the blank lines among them.
 * Not whatever else the drafter typed under the link; [] if there are none.
 */
function signatureLines(nodes) {
  let start = nodes.length;
  while (start > 0) {
    const node = nodes[start - 1];
    const blank = node.isTextblock && !node.textContent.trim();
    const right = node.isTextblock && node.attrs.textAlign === "right";
    if (!blank && !right) break;
    start -= 1;
  }
  const run = nodes.slice(start);
  return run.some((node) => node.textContent.trim()) ? run : [];
}

// The template's signature lines (HTML, see templateSignature) as nodes of
// this editor's schema, or null when there are none.
function signatureNodes(schema, html) {
  if (!html) return null;
  const holder = document.createElement("div");
  holder.innerHTML = html;
  const nodes = [];
  SchemaParser.fromSchema(schema)
    .parseSlice(holder)
    .content.forEach((node) => nodes.push(node));
  return nodes.length ? nodes : null;
}

// The first signature in the order column's pages, for a page that has none.
function anyCellSignature(cell, linkType) {
  const children = [];
  cell.forEach((child) => children.push(child));
  for (let i = 0; i < children.length; i += 1) {
    if (!findLink(children[i], linkType)) continue;
    let end = i + 1;
    while (end < children.length && !startsPage(children[end])) end += 1;
    const lines = signatureLines(children.slice(i + 1, end));
    if (lines.length) return lines;
  }
  return [];
}

const countPageStarts = (doc) => {
  let count = 0;
  doc.descendants((node) => {
    if (!node.isTextblock) return true;
    if (startsPage(node)) count += 1;
    return false;
  });
  return count;
};

export const PostMatterLinks = Extension.create({
  name: "postMatterLinks",
  // Its Ctrl+Enter ahead of PageBreak's.
  priority: 101,

  addOptions() {
    return {
      // The Book Hearing Room link, for a sheet with no link left to copy.
      getHref: () => "",
      // The template's signature lines as HTML (see templateSignature). Every
      // page the drafter adds gets these. Until they have loaded, a page
      // copies the signature lines from the sheet itself - its best guess,
      // the right-aligned lines under a link.
      getSignature: () => "",
      // Whether the draft is the ACCB order sheet. The Centre's other formats
      // (Award, Issues, Depositions...) share the editor but not its pages:
      // there a New page or Page break is a plain break, nothing added.
      isOrderSheet: () => true,
    };
  },

  addProseMirrorPlugins() {
    const { getHref, getSignature, isOrderSheet } = this.options;

    return [
      new Plugin({
        key: KEY,
        appendTransaction(transactions, oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          if (!isOrderSheet()) return null;
          // Only a page the drafter has just made gets a link: not a sheet
          // being loaded (setContent), not undo/redo, not our own insertion.
          if (
            transactions.some(
              (tr) =>
                tr.getMeta("preventUpdate") || tr.getMeta("history$") || tr.getMeta(KEY)
            )
          ) {
            return null;
          }
          if (countPageStarts(newState.doc) <= countPageStarts(oldState.doc)) {
            return null;
          }

          const { doc, schema } = newState;
          const linkType = schema.marks.link;
          const paragraph = schema.nodes.paragraph;
          if (!linkType || !paragraph) return null;

          // The page breaks that are new: not where an old one has moved to.
          const mapping = combineTransactionSteps(oldState.doc, transactions).mapping;
          const before = new Set();
          oldState.doc.descendants((node, pos) => {
            if (!node.isTextblock) return true;
            if (startsPage(node)) before.add(mapping.map(pos, 1));
            return false;
          });
          const added = [];
          doc.descendants((node, pos) => {
            if (!node.isTextblock) return true;
            if (startsPage(node) && !before.has(pos)) added.push(pos);
            return false;
          });

          const template = signatureNodes(schema, getSignature());

          // A break splits a page in two. The new page gets a link and the
          // signature lines as its last lines, if it has no link. So
          // does the old page, if its own moved down with the text after the
          // caret - but not if it had none to begin with: the drafter deleted
          // it. pos -> the signature lines to copy in after the link.
          const ends = new Map();
          added.forEach((pos) => {
            const $pos = doc.resolve(pos);
            const container = $pos.parent;
            if (CONTAINERS.indexOf(container.type.name) === -1) return;
            const index = $pos.index();
            const start = $pos.start();

            const children = [];
            container.forEach((child, offset) => children.push({ child, offset }));
            const isStart = (i) => startsPage(children[i].child);
            // Where a link goes to be the last line of a page ending before
            // child `i`.
            const endOf = (i) =>
              start + (i < children.length ? children[i].offset : container.content.size);

            // The page child `i` is on: children [first, end).
            const pageAt = (i) => {
              let first = i;
              while (first > 0 && !isStart(first)) first -= 1;
              let end = i + 1;
              while (end < children.length && !isStart(end)) end += 1;
              return [first, end];
            };
            // The signature lines under a page's link ([] if it has none of
            // its own), or null if the page has no link.
            const signature = ([first, end]) => {
              for (let i = first; i < end; i += 1) {
                if (findLink(children[i].child, linkType)) {
                  return signatureLines(
                    children.slice(i + 1, end).map(({ child }) => child)
                  );
                }
              }
              return null;
            };
            // Any page's, for a sheet whose split page had none.
            const anySignature = () => {
              for (let i = 0; i < children.length; i = pageAt(i)[1]) {
                const lines = signature(pageAt(i));
                if (lines && lines.length) return lines;
              }
              return [];
            };

            const newPage = pageAt(index);
            const moved = signature(newPage);
            const oldPage = index > 0 ? pageAt(index - 1) : null;
            if (!moved) {
              const lines = template || (oldPage && signature(oldPage));
              ends.set(endOf(newPage[1]), lines && lines.length ? lines : anySignature());
            } else if (oldPage && !signature(oldPage)) {
              ends.set(endOf(index), template || moved);
            }
          });
          if (!ends.size) return null;

          // Copy page 1's link: its href, less its id, and its paragraph's
          // formatting. Failing that, the href from the backend and the new
          // page's own formatting, left-aligned.
          const existing = findLink(doc, linkType);
          const href = existing
            ? withLinkId(existing.mark.attrs.href, "")
            : getHref();
          if (!href) return null;
          const source = existing ? existing.block : doc.nodeAt(added[0]);
          const attrs = {
            ...source.attrs,
            ...(existing ? {} : { textAlign: null }),
            style: withoutBreak(source.attrs.style),
          };

          const tr = newState.tr;
          Array.from(ends.keys())
            .sort((a, b) => b - a)
            .forEach((pos) => {
              const mark = linkType.create({ href: withLinkId(href, newLinkId()) });
              const link = paragraph.create(attrs, schema.text(LINK_TEXT, [mark]));
              tr.insert(pos, [link].concat(ends.get(pos)));
            });
          return tr.setMeta(KEY, true);
        },
      }),
    ];
  },

  addCommands() {
    return {
      /**
       * Page break (atCaret) or New page, in the order sheet.
       *
       * Ctrl+Enter in the order column splits the page at the caret, like
       * any page break (the plugin above then signs both halves). Anything
       * else - the New page button, or Ctrl+Enter outside the order column -
       * starts a new page right where the caret is: a page-break line, then
       * a fresh order table laid out like page 1's, with an empty line to
       * type on, its own "Post this matter" link and a copy of the signature
       * lines. From inside a table it goes after the table; an empty line
       * the caret is on becomes the page-break line. No table is split.
       */
      startOrderPage: (atCaret) => ({ state, tr, dispatch, commands }) => {
        const { schema } = state;
        const linkType = schema.marks.link;
        const { paragraph } = schema.nodes;
        const tables = orderTables(state.doc, linkType);
        const { $from } = state.selection;
        if (!this.options.isOrderSheet() || !tables.length || !paragraph || $from.depth < 1) {
          return atCaret ? commands.insertPageBreak() : commands.newPage();
        }
        const inOrderColumn = tables.some(
          ({ cell, cellPos }) => $from.pos > cellPos && $from.pos < cellPos + cell.nodeSize
        );
        if (atCaret && inOrderColumn) return commands.insertPageBreak();

        // Copy the order table above the caret (else the first one): its
        // layout, and the signature lines of its last page.
        const above = tables.filter((t) => t.pos < $from.pos);
        const { table, row, cell } = above.length ? above[above.length - 1] : tables[0];
        let last = -1;
        cell.forEach((child, offset, i) => {
          if (findLink(child, linkType)) last = i;
        });
        const found = findLink(cell.child(last), linkType);
        const under = [];
        cell.forEach((child, offset, i) => {
          if (i > last) under.push(child);
        });
        let signature = signatureNodes(schema, this.options.getSignature()) ||
          signatureLines(under);
        tables.some((t) => {
          if (!signature.length) signature = anyCellSignature(t.cell, linkType);
          return signature.length;
        });

        // Every new line is formatted like the "Post this matter" line, not
        // like the signature lines, which are right-aligned.
        const like = found.block.type === paragraph ? found.block.attrs : {};
        const plain = { ...like, style: withoutBreak(like.style) };
        const href = withLinkId(found.mark.attrs.href, newLinkId());
        const link = paragraph.create(
          plain,
          schema.text(LINK_TEXT, [linkType.create({ href })])
        );
        let column = null;
        const cells = [];
        row.forEach((rowCell) => {
          if (rowCell === cell) {
            column = rowCell.type.create(
              rowCell.attrs,
              [paragraph.create(plain), link].concat(signature)
            );
            cells.push(column);
            return;
          }
          const first = rowCell.firstChild;
          const attrs = first && first.type === paragraph
            ? { ...first.attrs, style: withoutBreak(first.attrs.style) }
            : {};
          cells.push(rowCell.type.create(rowCell.attrs, paragraph.create(attrs)));
        });
        const page = [
          paragraph.create({ ...like, style: withBreak(like.style) }),
          table.type.create(table.attrs, row.type.create(row.attrs, cells)),
        ];

        if (dispatch) {
          const top = $from.node(1);
          const to = $from.after(1);
          const from = top.type === paragraph && !top.content.size ? $from.before(1) : to;
          tr.replaceWith(from, to, page);
          // The empty line in the new order column: past the page-break
          // line, into the table and its row, past the cells before it.
          let at = from + page[0].nodeSize + 2;
          cells.some((c) => {
            if (c === column) return true;
            at += c.nodeSize;
            return false;
          });
          tr.setSelection(TextSelection.create(tr.doc, at + 2)).scrollIntoView();
        }
        return true;
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Enter": () => this.editor.commands.startOrderPage(true),
    };
  },
});

// The order sheet's tables - the top-level tables with a "Post this matter"
// link - as { table, pos, row, cell, cellPos }, cell being the order column.
function orderTables(doc, linkType) {
  const found = [];
  if (!linkType) return found;
  doc.forEach((table, pos) => {
    if (table.type.name !== "table") return;
    table.forEach((row, rowOffset) => {
      row.forEach((cell, cellOffset) => {
        if (found.length && found[found.length - 1].table === table) return;
        if (!findLink(cell, linkType)) return;
        found.push({ table, pos, row, cell, cellPos: pos + 2 + rowOffset + cellOffset });
      });
    });
  });
  return found;
}

/**
 * The order sheet's Page break and New page buttons: the built-in ones, kept
 * in the order column (see startOrderPage). Pass as toolbar customItems.
 */
export const ORDER_PAGE_ITEMS = {
  pagebreak: {
    ...BUILT_IN_ITEMS.pagebreak,
    run: (e) =>
      isPageBreak(e)
        ? e.chain().focus().removePageBreak().run()
        : e.chain().focus().startOrderPage(true).run(),
  },
  newpage: {
    ...BUILT_IN_ITEMS.newpage,
    run: (e) => e.chain().focus().startOrderPage(false).run(),
  },
};

/**
 * Give the clicked link an id of its own, if it has none yet or shares one
 * with a copy of itself, and return that id. Writes it into the document, so
 * it is saved with the sheet.
 */
function tagLink(editor, anchor) {
  const { state, view } = editor;
  const linkType = state.schema.marks.link;
  if (!linkType) return "";
  let pos;
  try {
    pos = view.posAtDOM(anchor, 0);
  } catch (err) {
    return "";
  }
  const range = getMarkRange(state.doc.resolve(pos), linkType);
  const node = range && state.doc.nodeAt(range.from);
  const mark = node && linkType.isInSet(node.marks);
  if (!mark) return "";

  const href = mark.attrs.href || "";
  const id = linkIdOf(href);
  if (id) {
    // Paragraphs carrying this id: more than one means a copied page.
    let copies = 0;
    state.doc.descendants((child) => {
      if (!child.isTextblock) return true;
      let carries = false;
      child.forEach((inline) => {
        const link = linkType.isInSet(inline.marks);
        if (link && linkIdOf(link.attrs.href) === id) carries = true;
      });
      if (carries) copies += 1;
      return false;
    });
    if (copies === 1) return id;
  }

  const fresh = newLinkId();
  view.dispatch(
    state.tr
      .addMark(range.from, range.to, linkType.create({ ...mark.attrs, href: withLinkId(href, fresh) }))
      // Bookkeeping, not an edit: Ctrl+Z should not take the id away again.
      .setMeta("addToHistory", false)
  );
  return fresh;
}

/**
 * Booking through the "Post this matter" links.
 *
 * Clicking a link opens Book Hearing Room in a new tab (a plain click would
 * navigate away and take the unsaved draft with it). When the drafter comes
 * back, the latest booking is written into the link they clicked - and only
 * if it is a booking they made after clicking, so coming back without booking
 * leaves the link as it was rather than copying another page's booking in.
 *
 * @param {object} options
 * @param {boolean} options.open whether the editor is open
 * @param {*} options.session changes when a different draft is opened
 * @param {string|null} options.lineUrl the post-matter-line endpoint
 * @param {string|null} options.templateUrl the order sheet template endpoint,
 *   for the signature lines new pages get
 * @param {() => string} options.getHtml the sheet as it stands
 * @param {(html: string) => void} options.setHtml
 * @returns {{ handleLinkClick: Function, getHref: () => string,
 *   getSignature: () => string }}
 *   handleLinkClick for OrderSheetEditor's onLinkClick, getHref and
 *   getSignature for PostMatterLinks
 */
export function usePostMatterLinks({
  open,
  session,
  lineUrl,
  templateUrl,
  getHtml,
  setHtml,
}) {
  // { id, baseline } from the click until the booking is written back.
  // baseline is the line as it was at the click: undefined while it loads,
  // null if it could not be read.
  const pendingRef = useRef(null);
  const hrefRef = useRef("");
  const signatureRef = useRef("");
  const latest = useRef({});
  latest.current = { lineUrl, getHtml, setHtml };

  useEffect(() => {
    pendingRef.current = null;
  }, [session]);

  // The Book Hearing Room link, for PostMatterLinks when the sheet has none.
  useEffect(() => {
    if (!open || !lineUrl) return undefined;
    let alive = true;
    instance
      .get(lineUrl)
      .then((res) => {
        const match = /href="([^"]*)"/.exec(res.data?.html || "");
        if (alive && match) {
          const holder = document.createElement("textarea");
          holder.innerHTML = match[1];
          hrefRef.current = holder.value;
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [open, lineUrl]);

  // The signature lines every new page gets, from the template.
  useEffect(() => {
    if (!open || !templateUrl) return undefined;
    let alive = true;
    instance
      .get(templateUrl)
      .then((res) => {
        if (alive) signatureRef.current = templateSignature(res.data?.content || "");
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [open, templateUrl]);

  // The link opens a new TAB; switching back fires "visibilitychange", not
  // the window "focus" event, so listen for both.
  useEffect(() => {
    if (!open) return undefined;
    const refresh = () => {
      const pending = pendingRef.current;
      if (!pending || pending.baseline === undefined) return;
      const { lineUrl: url, getHtml: read, setHtml: write } = latest.current;
      if (!url) return;
      instance
        .get(url)
        .then((res) => {
          const line = res.data?.html;
          // Not booked yet: the latest booking is still the one from before
          // the click, which belongs to whichever link it was made through.
          if (pendingRef.current !== pending || !line || line === pending.baseline) {
            return;
          }
          pendingRef.current = null;
          const current = read();
          let next = applyPostMatterHtml(current, line, pending.id);
          if (res.data?.stage) {
            next = applyPostedForStage(next, res.data.stage, pending.id);
          }
          if (next !== current) write(next);
        })
        .catch((err) =>
          console.error("Failed to refresh the post-matter line:", err)
        );
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [open]);

  const handleLinkClick = useCallback((href, event, editor) => {
    if (!href) return;
    const anchor =
      event && event.target && event.target.closest ? event.target.closest("a") : null;
    const url = latest.current.lineUrl;
    if (url && anchor && editor && isPostMatterText(anchor.textContent)) {
      const id = tagLink(editor, anchor);
      if (id) {
        const pending = { id, baseline: undefined };
        pendingRef.current = pending;
        instance
          .get(url)
          .then((res) => {
            pending.baseline = res.data?.html || "";
          })
          .catch(() => {
            pending.baseline = null;
          });
      }
    }
    window.open(href, "_blank", "noopener");
  }, []);

  const getHref = useCallback(() => hrefRef.current, []);
  const getSignature = useCallback(() => signatureRef.current, []);

  return { handleLinkClick, getHref, getSignature };
}
